/**
 * Formatting in the business's time zone and locale-neutral English. The server words its
 * sentences ("asked at 07:31") in that zone, so every clock on screen uses it too; until the
 * console knows it, the viewer's own zone stands in.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

let zone: string | undefined;

/** The business's time zone (an IANA name), from the signed-in tenant. */
export function setTimeZone(tz: string | null | undefined): void {
  if (!tz) {
    zone = undefined;
    return;
  }
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    zone = tz;
  } catch {
    zone = undefined;
  }
}

/** The zone clocks are shown in: the business's, or the viewer's own until it's known. */
export function displayZone(): string {
  return zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function clock(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: zone });
}

export function dayLabel(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: zone });
}

/** "Saturday, October 10" in the business's zone. */
export function longDay(d: Date): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: zone,
  });
}

/** "Friday, October 9, 2026 at 14:03:22", for a record that has to be exact. */
export function fullStamp(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: zone,
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: zone,
  });
  return `${day} at ${time}`;
}

/** The hour of the day, 0 to 23, in the business's zone. */
export function hourOf(d: Date): number {
  return Number(d.toLocaleString("en-GB", { hour: "2-digit", hourCycle: "h23", timeZone: zone }));
}

/** The calendar day, as YYYY-MM-DD, in the business's zone. */
export function dayKey(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: zone });
}

function sameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

/** "10:19" today, "Oct 3" otherwise. */
export function whenShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  return sameDay(d, now) ? clock(d) : dayLabel(d);
}

/** "now", "6 min ago", "2 h ago", "yesterday", "Oct 7". */
export function ago(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "never";
  const ms = now.getTime() - new Date(iso).getTime();
  if (ms < MINUTE) return "now";
  if (ms < HOUR) return `${Math.round(ms / MINUTE)} min ago`;
  if (ms < DAY && sameDay(new Date(iso), now)) return `${Math.round(ms / HOUR)} h ago`;
  if (ms < 2 * DAY) return "yesterday";
  return dayLabel(iso);
}

/** "7 h 41 min", "23 min", "2 d 4 h". Negative durations read as overdue. */
export function duration(ms: number): string {
  const abs = Math.abs(ms);
  const d = Math.floor(abs / DAY);
  const h = Math.floor((abs % DAY) / HOUR);
  const m = Math.floor((abs % HOUR) / MINUTE);
  const text = d > 0 ? `${d} d ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
  return ms < 0 ? `${text} over` : text;
}

/** "6 min 40 s" for short spans. */
export function elapsed(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m >= 60) return duration(ms);
  return m > 0 ? `${m} min ${rest} s` : `${rest} s`;
}

export function timeLeft(due: string | null | undefined, now: Date = new Date()): string | null {
  if (!due) return null;
  return duration(new Date(due).getTime() - now.getTime());
}

export function percent(x: number | null | undefined, digits = 0): string {
  if (x === null || x === undefined) return "not run";
  return `${(x * 100).toFixed(digits).replace(/\.0$/, "")}%`;
}

export function count(n: number): string {
  return n.toLocaleString("en-US");
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}
